import { useState } from 'react';
import { Breadcrumb } from '../components/marketing/Breadcrumb';
import { CTABanner } from '../components/marketing/CTABanner';
import { SEO } from '../components/SEO';
import { JsonLd } from '../components/JsonLd';
import { PricingCards } from '../components/landing/Pricing';
import { SavingsCalculator } from '../components/landing/SavingsCalculator';
import { PRICING_FAQ, PRICING_PAGE_COPY } from '../data/pricing-content';

const PRICING_FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: PRICING_FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
};

export function PricingPage(): JSX.Element {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEO
        title={PRICING_PAGE_COPY.seoTitle}
        description={PRICING_PAGE_COPY.seoDescription}
        path="/pricing"
      />
      <JsonLd data={PRICING_FAQ_SCHEMA} />

      {/* Hero */}
      <section className="pt-12 pb-16 bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: PRICING_PAGE_COPY.breadcrumbHome, href: '/' },
              { label: PRICING_PAGE_COPY.breadcrumbCurrent },
            ]}
          />
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
              {PRICING_PAGE_COPY.heading}
            </h1>
            <p className="text-lg text-[var(--landing-text-muted)]">
              {PRICING_PAGE_COPY.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing cards — real prices from /api/public/pricing, skeleton until loaded */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="sr-only">{PRICING_PAGE_COPY.plansHeading}</h2>
          <PricingCards ctaTestId="pricing-page-cta" ctaLocation="pricing_page" />
        </div>
      </section>

      {/* Savings calculator — renders only once a paid plan's live price is known */}
      <SavingsCalculator />

      {/* Pricing FAQ */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--landing-text-primary)] text-center mb-12">
            {PRICING_PAGE_COPY.faqHeading}
          </h2>
          <div className="max-w-3xl mx-auto">
            {PRICING_FAQ.map((item, i) => (
              <div key={i} className="border-b border-[var(--landing-border)]">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full text-left py-4 flex items-center justify-between min-h-[44px] text-[var(--landing-text-primary)] hover:text-[var(--landing-accent-text)] transition-colors"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium pr-4">{item.question}</span>
                  <span
                    className="text-xl font-light flex-shrink-0 text-[var(--landing-accent-text)]"
                    aria-hidden="true"
                  >
                    {openFaq === i ? '−' : '+'}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="pb-4 text-[var(--landing-text-muted)] leading-relaxed">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
